import { Component, Output, OnInit, Input, ViewChild, ElementRef, EventEmitter } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';

import { RankingNode } from '../dialogs/cpq-ranking-dialog/cpq-ranking.model';
import { ActionItem } from '../cpq-datatable/action-menu/action-links.model';
import { AutoScrollService } from '../common/services/auto-scroll.service';
import { clone } from '../common/functions/common-helpers';
import { any, sort } from '../common/functions/js-linq';
import { OrderDirection } from '../common/models/DataTableFilter.model';
import { SelectType } from '../cpq-select/cpq-select.component';
import { ILookup } from '../common/interfaces/ILookup.interface';

@Component({
    selector: 'cpq-ranking',
    templateUrl: './cpq-ranking.component.html',
    styleUrls: ['./cpq-ranking.component.scss', '../dialogs/cpq-ranking-dialog/cpq-ranking-dialog.component.scss'],
    providers: [DragulaService, AutoScrollService],
    standalone: false
})
export class CpqRankingComponent implements OnInit {
  @Input('nodes') nodes: Array<RankingNode>;
  @Input('hasSearch') hasSearch? = true;
  @Input('id') id: number;
  @Input('disableRankingInput') disableRankingInput? = false;
  /** If the ranks should be unique, set the 'rankIsUnique' property to 'true'*/
  @Input('RankIsUnique') rankIsUnique = false;

  /**
 * Label for the required column. 
 * Default value is 'Value'.
 */
  @Input('valueLabel') valueLabel? = 'xsltValue';

  /**
 * Determines whether to show the "Move to Top" and "Move to Bottom" actions.
 * Default value is `true`.
 */
  @Input('showActionItems') showActionItems? = true;

  /**
 * Determines whether to show the "Remove Node" action, 
 * i.e., the action to remove a single row.
 * Default value is `false`.
 */
  @Input('showRemoveNodeActionItem') showRemoveNodeActionItem? = false;

  /**
 * By default, the table has three columns:
 * - Actions
 * - Rank
 * - Value
 * 
 * If additional columns are needed (for example, on the 
 * "Report Module -> Ordering Columns" page), this input can be used.
 * 
 * The input type is `ILookup<Key = string, Value = string>[]`:
 * - `Key` represents a property name of the `RankingNode` object, 
 *   such as `id`, `name`, `position`, etc.
 * - `Value` represents the translation key used as the column label in the UI.
 * 
 * Example usage:
 * ```ts
 * this.dynamicColumns = [
 *   { id: 'orderType', text: 'ReportModule.OrderColumns.OrderType' }
 * ];
 * ```
 * This input allows dynamically adding columns without hardcoding them in the template.
 */
  @Input('dynamicColumns') dynamicColumns? = new Array<ILookup>();

  @Output('onRank') onRank = new EventEmitter<Array<RankingNode>>();
  @ViewChild('listHolder', { static: true }) listHolder: ElementRef;
  @ViewChild('viewPort', { static: true }) viewPort: CdkVirtualScrollViewport;

  public filteredList = new Array<RankingNode>();
  public actionItems: Array<ActionItem> = [];
  public unfilteredList: Array<ILookup> = [];
  public SelectType = SelectType;
  public selectedNode: number;
  private subscriptions = new Subscription();

  constructor(private dragulaService: DragulaService,
    private autoScrollService: AutoScrollService) {
  }

  ngOnInit() {
    this.reorderNodes(false);
    this.initActionItems();
    this.initSubscriptions();
    this.autoScrollService.init(this.dragulaService, this.listHolder);
    this.initDragula();
    this.initLookpusForSearch();
 }

  private initActionItems() {
    this.actionItems = [
      {
        key: 'xsltMoveToTop',
        click: (row: RankingNode) => this.moveToTopOrBottom(row, true)
      },
      {
        key: 'xsltMoveToBottom',
        click: (row: RankingNode) => this.moveToTopOrBottom(row, false)
      }
    ];
  }

  private initSubscriptions() {
    this.subscriptions.add(this.dragulaService.drop("ranking-bag").subscribe(({ el }) => {
      const id = el.getAttribute('data-id');
      const name = el.getAttribute('data-name');
      const position = this.getRank(el);
      this.onRankChange(new RankingNode(id, name, position), true);
    }));
  }

  private initDragula() {
    this.dragulaService.createGroup("ranking-bag", {
      moves: (el, source, handle) => {
        return !handle.className.includes("rank-input-field");
      },
      invalid: (el) => {
        return el.classList.contains('no-drag');
      },
      accepts: (el, handle, source, sibling) => {
        if (!sibling) return true;
        return !sibling.classList.contains('no-drag');
      }
    })
  }

  itemChosen(itemId: any) {
    this.nodes.forEach(node => node.highlighted = false);
    var item = this.nodes.find(x => x.id == itemId);
    item.highlighted = true;
    var newRankIndex = this.nodes.indexOf(item);
    if (newRankIndex !== -1) {
      this.viewPort.scrollToIndex(newRankIndex);
    }

    setTimeout(() => item.highlighted = false, 2000)
  }

  onRankChange(item: RankingNode, isNodeDragged: boolean = false) {
    // check for prevent drag position
    let isPositionForPreventDrag = this.filteredList.some(node => node.preventDrag && node.position === +item.position);
    if (isPositionForPreventDrag) {
      item.position = this.filteredList.find(node => node.id == item.id).position;
      this.reorderNodes(isNodeDragged);
      return;
    }

    let rankedNode = this.nodes.find(node => node.id == item.id);
    if (rankedNode) {
      rankedNode.position = +item.position;
      rankedNode.highlighted = true;

      this.reorderNodes(isNodeDragged);
      setTimeout(() => this.nodes.find(node => node.id == item.id).highlighted = false, 2000)
    }
  }

  private reorderNodes(isNodeDragged: boolean) {
    this.filteredList = clone(this.nodes);
    sort(this.nodes, 'position', OrderDirection.ASC, this.rankIsUnique || isNodeDragged ? null : 'name');
    this.nodes = [...this.nodes];
    this.onRank.emit(this.nodes);
  }

  private getRank(element: Element): number {
    const previousSiblingRank = element.previousElementSibling && +element.previousElementSibling.getAttribute('data-position');
    const nextSiblingRank = element.nextElementSibling && +element.nextElementSibling.getAttribute('data-position');

    if (!previousSiblingRank) {
      return 1;
    }

    return previousSiblingRank === nextSiblingRank ? previousSiblingRank : previousSiblingRank + 1;
  }

  private moveToTopOrBottom(item: RankingNode, moveToTop: boolean) {
    const position = this.rankIsUnique ? 0 : 1;
    item.position = moveToTop ? position : this.nodes[this.nodes.length - 1].position + 1;
    this.onRankChange(item, false);
  }

  private initLookpusForSearch() {
    this.nodes.forEach(node => {
      this.unfilteredList.push({
        id: node.id,
        text: (!this.dynamicColumns || this.dynamicColumns.length === 0) ? node.name : this.GetSearchText(node)
      })
    })
  }

  private GetSearchText(node: RankingNode): string {
    return node.name + ' | ' + this.dynamicColumns
    .map(column => node[column.id])
    .filter(value => !!value)
    .join(' | ');
  }

  removeNode(item: RankingNode) {
    const removedNodePosition = item.position;
    this.nodes = this.nodes.filter(node => node.id !== item.id);

    if (this.nodes.length === 0) {
      this.onRank.emit(this.nodes);
      return;
    }

    this.nodes.forEach(node => {
      if (node.position > removedNodePosition) {
        node.position -= 1;
        this.onRankChange(node, false);
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
